// libs
import get from 'lodash/get';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

// constants
import {
  FILTER_TYPES,
  CATEGORY_FILTER_KEY,
  FILTER_ACTION_TYPE,
  FILTER_UI_TYPES,
  ESCAPE_FILTERS,
  FILTER_KEYS,
  SORT_FILTERS,
  ANALYTICS_KEY_JOIN,
  ANALYTICS_VALUE_JOIN,
  ANALYTICS_JOIN,
  COLOR_FILTER_KEY,
  PRICE_FILTER_KEY,
  DP_HOME,
} from 'shared/layouts/Listing/constants';
import { IS_FILTERED_URL } from 'shared/layouts/Listing/Api/constants';

// helpers
import { getPLPFilterParams } from 'shared/helpers/utils/url';
import { generateSameHostUrl } from 'shared/helpers/utils';

// defs
import { IDisplayAppliedFilters, IGetDisplayAppliedFilters, IFilterIds } from './types';
import { ILandingPageWidget } from 'shared/components/AdPlatformWidgets/types';
import { IAdplatformDataProps } from 'shared/store/listing/types';

export const getFilterType = (filter: Listing.IFilter): FILTER_TYPES => {
  switch (true) {
    case get(filter, 'actionType', '').toLowerCase() === FILTER_ACTION_TYPE: {
      return FILTER_TYPES.MORE;
    }

    case get(filter, 'key', '').toLowerCase() === CATEGORY_FILTER_KEY: {
      return FILTER_TYPES.CATEGORY;
    }

    case get(filter, 'type', '').toLowerCase() === FILTER_UI_TYPES.MULT_SLECT: {
      return FILTER_TYPES.MULTI_SELECT;
    }

    case get(filter, 'type', '').toLowerCase() === FILTER_UI_TYPES.RANGE_FILTER: {
      return FILTER_TYPES.RANGE_FILTER;
    }

    case get(filter, 'type', '').toLowerCase() === FILTER_UI_TYPES.SINGLE_SELECT: {
      return FILTER_TYPES.SINGLE_SELECT;
    }

    default: {
      return FILTER_TYPES.NONE;
    }
  }
};

export const getFilterKey = (filter: Listing.IFilter) =>
  filter && filter.key && filter.key !== '' ? filter.key : FILTER_ACTION_TYPE;

export const isSelectedFilter = (filter: Listing.IFilter, currentFilter: string) => {
  if (
    currentFilter === FILTER_ACTION_TYPE &&
    get(filter, 'actionType', '').toLowerCase() === FILTER_ACTION_TYPE
  ) {
    return true;
  }

  return get(filter, 'key', '').toLowerCase() === currentFilter.toLowerCase();
};

export const getSelectedFilter = (filters: Listing.IFilter[], currentFilter: string) => {
  if (currentFilter === FILTER_ACTION_TYPE) {
    return find(
      filters,
      (filter) => get(filter, 'actionType', '').toLowerCase() === FILTER_ACTION_TYPE
    );
  }

  let selectedFilter = find(
    filters,
    (filter) => get(filter, 'key', '').toLowerCase() === currentFilter
  );

  // when user clicks on next tab we apply the filters from current tab
  // under certain cases the new tab clicked wont be present after current tabs filter is applied
  // in those cases we fall back to first tab
  if (selectedFilter === undefined) {
    selectedFilter = filters[0];
  }

  return selectedFilter;
};

export const isValidRangeFilter = (filterValue: any) => {
  if (isEmpty(filterValue)) return false;
  return filterValue.hasOwnProperty('min') || filterValue.hasOwnProperty('max');
};

export const getFilterCount = (filter: Listing.IFilter, appliedFilters: any) => {
  const type = getFilterType(filter);
  const filterKey = get(filter, 'key', '').toLowerCase();

  if (type === FILTER_TYPES.RANGE_FILTER) {
    return isEmpty(get(appliedFilters, filterKey, {})) ||
      !isValidRangeFilter(appliedFilters[filterKey])
      ? 0
      : 1;
  }

  if (
    type !== FILTER_TYPES.CATEGORY &&
    type !== FILTER_TYPES.MULTI_SELECT &&
    type !== FILTER_TYPES.SINGLE_SELECT
  ) {
    return 0;
  }

  return get(appliedFilters, filterKey, []).length;
};

export const isFilterApplied = (appliedFilters: Listing.IAppliedFilters) => {
  return (
    Object.keys(appliedFilters).filter((filterKey) => {
      if (ESCAPE_FILTERS.includes(filterKey)) return false;
      if (PRICE_FILTER_KEY === filterKey) {
        const filterValue = appliedFilters[filterKey];

        if (!isValidRangeFilter(filterValue)) return false;
      }
      return !isEmpty(appliedFilters[filterKey]);
    }).length > 0
  );
};

export const validateLocalAppliedFilters = (
  appliedFilters: Listing.IAppliedFilters,
  localAppliedFilters: Listing.IAppliedFilters
) => {
  let localValidFilters: Listing.IAppliedFilters = {};

  try {
    Object.keys(localAppliedFilters).forEach((filterKey) => {
      // if filter is of the type sort and page size
      if (ESCAPE_FILTERS.includes(filterKey)) {
        localValidFilters[filterKey] = localAppliedFilters[filterKey];
      }

      // if filter is the range filter type, do not validate and push
      if (filterKey === PRICE_FILTER_KEY) {
        localValidFilters[filterKey] = localAppliedFilters[filterKey];
      }

      // for the default case, validate if the value to filters are valid
      if (!isEmpty(localAppliedFilters[filterKey])) {
        localValidFilters[filterKey] = localAppliedFilters[filterKey];
      }
    });
  } catch (_) {
    localValidFilters = localAppliedFilters;
  }

  return !isEqual(localValidFilters, appliedFilters);
};

export const isAppliedFiltersChanged = (
  filter: Listing.IFilter,
  tempfilters: string | number | string[],
  appliedFilters: Listing.IAppliedFilters
) => {
  const filterKey = get(filter, 'key', '');

  switch (getFilterType(filter)) {
    case FILTER_TYPES.MULTI_SELECT:
    case FILTER_TYPES.CATEGORY:
    case FILTER_TYPES.SINGLE_SELECT: {
      const initialFilters = get(appliedFilters, filterKey, []);

      if (initialFilters.length !== tempfilters.length) {
        return true;
      }

      return (
        tempfilters.filter((value) => initialFilters.includes(value)).length !==
        initialFilters.length
      );
    }

    case FILTER_TYPES.RANGE_FILTER: {
      const initialFilters = get(appliedFilters, filterKey, {});

      // check if both either are objects or empty
      if (isEmpty(tempfilters) !== isEmpty(initialFilters)) {
        return true;
      }

      return (
        get(tempfilters, 'min') !== get(initialFilters, 'min') ||
        get(tempfilters, 'max') !== get(initialFilters, 'max')
      );
    }

    default: {
      return false;
    }
  }
};

export const getSelectedSort = (appliedFilters: Listing.IAppliedFilters) => {
  const sortValue = get(appliedFilters, FILTER_KEYS.SORT);

  if (sortValue === undefined) {
    return SORT_FILTERS[0];
  }

  let sort = find(SORT_FILTERS, (filter) => filter.value === sortValue);

  if (sort !== undefined) {
    return sort;
  }

  return SORT_FILTERS[0];
};

export const stringifyFilters = (appliedFilters: Listing.IAppliedFilters) => {
  if (isEmpty(appliedFilters)) return '';

  const selectedFilters = Object.keys(appliedFilters);

  return selectedFilters
    .filter((filterKey) => ESCAPE_FILTERS.includes(filterKey) === false)
    .map((filterKey) => {
      const value = appliedFilters[filterKey];

      if (isEmpty(value)) {
        return '';
      }

      let sValue = value;

      if (Array.isArray(value)) {
        sValue = value.join(ANALYTICS_VALUE_JOIN);
      } else if (typeof value === 'object') {
        sValue = `${value.min}${ANALYTICS_VALUE_JOIN}${value.max}`;
      }

      return `${filterKey}${ANALYTICS_KEY_JOIN}${sValue}`;
    })
    .join(ANALYTICS_JOIN);
};

export const compare = (a: string, b: string) => {
  const tmpA = Number(a);
  const tmpB = Number(b);

  if (tmpA < tmpB) {
    return -1;
  }

  if (tmpA > tmpB) {
    return 1;
  }

  return 0;
};

export const getAllIds = (filter: Listing.IValue[]) => {
  let ids: string[] = [];

  if (isEmpty(filter)) return ids;

  filter.forEach((value) => {
    if (value.children && value.children.length > 0) {
      const childIds = getAllIds(value.children);
      ids = [...ids, ...childIds];
    } else {
      ids.push(value.id);
    }
  });

  return ids;
};

export const getActionURLWithPLPFilters = (
  item: Listing.IBannerImage | Listing.IProduct,
  key: string
) => {
  const FilterData = get(item, 'filter_data', '');
  const sortData = get(item, 'sort', '');
  const URL = get(item, key, '');

  if (FilterData === '' && sortData === '') {
    return generateSameHostUrl(URL);
  }

  const params = new URLSearchParams();
  const JOIN_CHAR = URL.indexOf('?') > -1 ? '&' : '?';

  params.set('f', getPLPFilterParams(FilterData, sortData));

  params.set(IS_FILTERED_URL, 'true');

  return generateSameHostUrl(URL + JOIN_CHAR + params.toString());
};

export const FILTER_KEY = 'PLP-DISPLAY-FILTERS';

export const flattenCategoryValue = (
  filterValues: Listing.IValue[] | Listing.IChild[]
): { [key: string]: string } => {
  let flattenedData: { [key: string]: string } = {};

  filterValues.forEach((filterValue) => {
    flattenedData[String(filterValue.id)] = filterValue.name;
    if (isEmpty(filterValue.children) === false) {
      flattenedData = {
        ...flattenedData,
        ...flattenCategoryValue(filterValue.children),
      };
    }
  });

  return flattenedData;
};

export const indexDisplayFilters = (displayAppliedFilters: IDisplayAppliedFilters[] = []) => {
  const index: { [key: string]: number[] } = {};

  displayAppliedFilters.forEach((filter: IDisplayAppliedFilters, idx: number) => {
    if (index[filter.key] === undefined) {
      index[filter.key] = [];
    }

    index[filter.key].push(idx);
  });

  return index;
};

export const getDisplayAppliedFilters = ({
  appliedFilters,
  filters,
  displayAppliedFilters = [],
}: IGetDisplayAppliedFilters): IDisplayAppliedFilters[] | never[] => {
  let newDisplayAppliedFilters = [...displayAppliedFilters];
  const displayAppliedFiltersIndex = indexDisplayFilters(newDisplayAppliedFilters);

  const filtersAppliedPointer: string[] = [];
  newDisplayAppliedFilters.forEach((displayFilter) => {
    // @ts-ignore
    filtersAppliedPointer.push(displayFilter.id);
  });

  const selectedFilters = Object.keys(appliedFilters);
  const existingAppliedFiltersKeys = Object.keys(displayAppliedFiltersIndex);

  // remove stale filters
  existingAppliedFiltersKeys.forEach((filterKey) => {
    if (
      isEmpty(appliedFilters[filterKey]) ||
      // we would be skipping filters page and sort
      ESCAPE_FILTERS.includes(filterKey)
    ) {
      // setting undefined - would clear once all filters are run through loop
      displayAppliedFiltersIndex[filterKey].forEach(
        // @ts-ignore
        (idx) => (newDisplayAppliedFilters[idx] = undefined)
      );
      return;
    }

    // if filter is a single value filter and it exists we dont need to check childs
    if (Array.isArray(appliedFilters[filterKey]) === false) {
      return;
    }

    displayAppliedFiltersIndex[filterKey].forEach((idx) => {
      // we are checking isArray in above condition
      // @ts-ignore
      if (appliedFilters[filterKey].includes(newDisplayAppliedFilters[idx].id) === false) {
        // setting undefined - would clear once all filters are run through loop
        // @ts-ignore
        newDisplayAppliedFilters[idx] = undefined;
      }
    });
  });

  // clear all undefined filters
  newDisplayAppliedFilters = newDisplayAppliedFilters.filter((value) => value !== undefined);

  return filters
    .filter((filter) => selectedFilters.includes(get(filter, 'key', '')))
    .reduce((accumulator: IDisplayAppliedFilters[], filter) => {
      const filterType = getFilterType(filter);
      const filterKey = get(filter, 'key', '');

      let newValue = accumulator;

      switch (filterType) {
        case FILTER_TYPES.CATEGORY: {
          const categoryValues = get(filter, 'values', []);
          const flattenedCategories = flattenCategoryValue(categoryValues);

          // for category appliedFilters is array
          // @ts-ignore
          get(appliedFilters, filterKey, []).forEach((filterId: number) => {
            // already is display list dont add again
            if (filtersAppliedPointer.includes(String(filterId))) {
              return;
            }

            if (flattenedCategories[filterId] !== undefined) {
              newValue.push({
                key: filterKey,
                id: String(filterId),
                value: flattenedCategories[filterId],
              });
              filtersAppliedPointer.push(String(filterId));
            }
          });
          break;
        }

        case FILTER_TYPES.SINGLE_SELECT:
        case FILTER_TYPES.MULTI_SELECT: {
          // for single select and multi select appliedFilters is array
          // @ts-ignore
          get(appliedFilters, filterKey, []).forEach((filterId: number) => {
            // already is display list dont add again
            if (filtersAppliedPointer.includes(String(filterId))) {
              return;
            }

            const filterValue = filter.values.find((value) => value.id === filterId);

            if (isEmpty(filterValue) === false) {
              let filterMeta = {};

              if (filterKey === COLOR_FILTER_KEY) {
                const colorCode =
                  isEmpty(filterValue.color_code) === false ? filterValue.color_code : undefined;
                const colorImage =
                  isEmpty(filterValue.color_image) === false ? filterValue.color_image : undefined;

                filterMeta = {
                  meta: {
                    colorCode,
                    colorImage,
                  },
                };
              }

              newValue.push({
                key: filterKey,
                id: String(filterId),
                value: filterValue.name,
                ...filterMeta,
              });
            }
            filtersAppliedPointer.push(String(filterId));
          }, {});
          break;
        }

        case FILTER_TYPES.RANGE_FILTER: {
          // Range filter appliedFilters are stored as object
          // @ts-ignore
          const { min, max } = appliedFilters[filterKey];

          if (min === undefined || max === undefined) {
            break;
          }
          // @ts-ignore
          newValue.push({
            key: filterKey,
            value: `${min} - ${max}`,
          });
          break;
        }

        default: {
          if (typeof appliedFilters[filterKey] !== 'object') {
            newValue.push({
              key: filterKey,
              value: appliedFilters[filterKey],
            });
          }
        }
      }

      return newValue;
    }, newDisplayAppliedFilters);
};

interface IFilterMeta {
  [key: string]: {
    colorCode?: string;
    colorImage?: string;
  };
}

export const evaluateDisplayFilters = ({
  filterKey,
  filters,
  displayAppliedFilters,
  filterIds,
}: {
  filterKey: string;
  filters: Listing.IFilter[];
  displayAppliedFilters: IDisplayAppliedFilters[];
  filterIds?: IFilterIds;
}) => {
  let localDisplayAppliedFilters: IDisplayAppliedFilters[] = [...displayAppliedFilters];

  let indexedDisplayFilters = indexDisplayFilters(localDisplayAppliedFilters);

  // dont do anything for escaped filters
  if (ESCAPE_FILTERS.includes(filterKey)) {
    return localDisplayAppliedFilters;
  }

  // if price filter we dont need array manipulation
  if (filterKey === PRICE_FILTER_KEY) {
    if (indexedDisplayFilters[filterKey] !== undefined) {
      // remove existing price filter from display array
      localDisplayAppliedFilters = localDisplayAppliedFilters.filter(
        (_, idx) => idx !== indexedDisplayFilters[filterKey][0]
      );
    }

    // if empty price filter value dont add anything to display list
    if (isEmpty(filterIds)) {
      return localDisplayAppliedFilters;
    }

    // @ts-ignore
    const { min, max } = filterIds;

    // add new price filter value in display list
    localDisplayAppliedFilters.push({
      key: PRICE_FILTER_KEY,
      value: `${min} - ${max}`,
    });

    return localDisplayAppliedFilters;
  }

  // if filter values are string or numbers
  if (typeof filterIds === 'string' || typeof filterIds === 'number') {
    if (indexedDisplayFilters[filterKey] !== undefined) {
      // remove existing filter from display array
      localDisplayAppliedFilters = localDisplayAppliedFilters.filter(
        (_, idx) => idx !== indexedDisplayFilters[filterKey][0]
      );
    }

    // if empty filter value dont add anything to display list
    if (isEmpty(filterIds)) {
      return localDisplayAppliedFilters;
    }

    // add new filter value in display list
    // @ts-ignore
    localDisplayAppliedFilters.push({
      key: filterKey,
      value: filterIds,
    });

    return localDisplayAppliedFilters;
  }

  // remove stale filterIds
  if (indexedDisplayFilters[filterKey] !== undefined && Array.isArray(filterIds)) {
    // filterIds would only by array
    // @ts-ignore
    const stringFilterIds = filterIds.map((id) => String(id));

    indexedDisplayFilters[filterKey].forEach((idx) => {
      if (
        // id would always be available for non price filters
        // @ts-ignore
        stringFilterIds.includes(localDisplayAppliedFilters[idx].id) === false
      ) {
        // we are setting undefined for future clearing the value
        // else if we delete here indexedDisplayFilters will have stale data
        // @ts-ignore
        localDisplayAppliedFilters[idx] = undefined;
      }
    });

    // clear all undefined values
    localDisplayAppliedFilters = localDisplayAppliedFilters.filter((value) => value !== undefined);

    indexedDisplayFilters = indexDisplayFilters(localDisplayAppliedFilters);
  }

  // get filterIds not already pushed to display array
  // @ts-ignore
  const newFilterIds: (number | string)[] = filterIds.filter(
    (filterId: number | string) =>
      indexedDisplayFilters[filterKey] === undefined ||
      indexedDisplayFilters[filterKey].some(
        // id would always be available for non price filters
        // @ts-ignore
        (idx) => localDisplayAppliedFilters[idx].id === String(filterId)
      ) === false
  );

  if (newFilterIds.length === 0) {
    return localDisplayAppliedFilters;
  }

  const filter = filters.find((tempFilter) => tempFilter.key === filterKey);

  if (filter === undefined) {
    return localDisplayAppliedFilters;
  }

  let filterValues: { [key: string]: string } = {};
  let filterMeta: IFilterMeta = {};

  switch (getFilterType(filter)) {
    case FILTER_TYPES.CATEGORY: {
      filterValues = flattenCategoryValue(get(filter, 'values', []));
      break;
    }

    case FILTER_TYPES.MULTI_SELECT:
    case FILTER_TYPES.SINGLE_SELECT: {
      const values = filter.values || [];

      filterValues = values.reduce((acc: { [key: number]: string }, value: Listing.IValue) => {
        acc[value.id] = value.name;
        return acc;
      }, {});

      if (filterKey === COLOR_FILTER_KEY) {
        filterMeta = values.reduce((acc: IFilterMeta, value: Listing.IValue) => {
          const colorCode = isEmpty(value.color_code) === false ? value.color_code : undefined;
          const colorImage = isEmpty(value.color_image) === false ? value.color_image : undefined;

          acc[value.id] = { colorCode, colorImage };
          return acc;
        }, {});
      }

      break;
    }
  }

  if (isEmpty(filterValues)) {
    return localDisplayAppliedFilters;
  }

  const newDisplayAppliedFilters: IDisplayAppliedFilters[] = [];
  newFilterIds.forEach((filterId: string | number) => {
    if (filterValues[filterId] === undefined) {
      return;
    }

    newDisplayAppliedFilters.push({
      key: filterKey,
      id: String(filterId),
      value: filterValues[filterId],
      ...(filterKey === COLOR_FILTER_KEY ? { meta: filterMeta[filterId] } : {}),
    });
  });

  if (newDisplayAppliedFilters.length === 0) {
    return localDisplayAppliedFilters;
  }

  const finalDisplayAppliedFilters = [...localDisplayAppliedFilters, ...newDisplayAppliedFilters];

  return finalDisplayAppliedFilters;
};

export const getTipTileURL = (url: string) => {
  const params = new URLSearchParams();
  // adding query string to all URL for next page view event
  params.append('ptype', 'TIPTILE');

  const JOIN_CHAR = url.indexOf('?') > -1 ? '&' : '?';

  return `${url}${JOIN_CHAR}${params.toString()}`;
};

export const getTransformedAdPlatformWidgets = (
  widgets: ILandingPageWidget[],
  adPlatFormData: IAdplatformDataProps,
  dataBannerName: string,
  dwebDataBannerName?: string,
  subtitle?: string
) => {
  const transformedWidgets = widgets.map((widget: ILandingPageWidget) => {
    const { inventory_name, widget_data, ...rest } = widget;
    if (inventory_name === dataBannerName || inventory_name === dwebDataBannerName) {
      return {
        inventory_name,
        ...rest,
        widget_data: {
          ...widget_data,
          parameters: {
            ...widget_data.parameters,
            title: adPlatFormData.title ? adPlatFormData.title : '',
            sub_title: subtitle
              ? subtitle
              : adPlatFormData.sub_title
                ? adPlatFormData.sub_title
                : '',
            title_align: 'center',
            title_align_desktop: 'center',
            typography: 'type242',
          },
        },
      };
    }
    return widget;
  });
  return transformedWidgets;
};

export const getDynamicPageBreadCrumbs = (keyWord: string) => {
  return [
    DP_HOME,
    {
      key: keyWord
    },
  ];
}
